import {
  ChangeEvent,
  FocusEvent,
  FormEvent,
  KeyboardEvent,
  useCallback,
  useRef,
  useState,
} from "react";
import { NavigateFunction } from "react-router-dom";
import { AiChatSessionService } from "../services/aiChatSessionService";
import IGlobalConfig from "../types/IGlobalConfig";

const COMPOSER_MAX_HEIGHT_PX = 132;

function resizeComposerField(field: HTMLElement) {
  field.style.height = "auto";
  field.style.height = `${Math.min(field.scrollHeight, COMPOSER_MAX_HEIGHT_PX)}px`;
}

function chatErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof Error) || !error.message) {
    return fallback;
  }
  const message = error.message.trim();
  if (message.includes("NOT_CHARITY_USER")) {
    return message.replace(/^NOT_CHARITY_USER\s*\/\s*/i, "").trim() || fallback;
  }
  return message || fallback;
}

interface UseLlmChatPromptParams {
  config: IGlobalConfig;
  serviceAccessToken: string;
  charityId?: string;
  navigate: NavigateFunction;
}

interface UseLlmChatPromptReturn {
  message: string;
  loading: boolean;
  error: string | null;
  focused: boolean;
  handleMessageChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (event: FormEvent) => void;
  handleComposerKeyDown: (event: KeyboardEvent<HTMLFormElement>) => void;
  handleFocus: () => void;
  handleComposerBlur: (event: FocusEvent<HTMLDivElement>) => void;
  handleSuggestedPrompt: (prompt: string) => void;
  handleViewConversations: () => void;
}

export function useLlmChatPrompt({
  config,
  serviceAccessToken,
  charityId,
  navigate,
}: UseLlmChatPromptParams): UseLlmChatPromptReturn {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);
  const loadingRef = useRef(false);

  const submitPrompt = useCallback(
    async (rawMessage: string) => {
      const trimmed = rawMessage.trim();
      if (!trimmed || loadingRef.current) {
        return;
      }

      if (!charityId) {
        setError("Select a charity before starting a chat.");
        return;
      }

      loadingRef.current = true;
      setMessage(trimmed);
      setLoading(true);
      setError(null);

      try {
        const result = await AiChatSessionService.createSession(
          config,
          serviceAccessToken,
          charityId,
          { message: trimmed }
        );
        navigate(`/chat-session/${result.sessionId}`);
      } catch (err) {
        loadingRef.current = false;
        setError(
          chatErrorMessage(err, "Failed to start chat. Please try again.")
        );
        setLoading(false);
      }
    },
    [charityId, config, navigate, serviceAccessToken]
  );

  const handleMessageChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setMessage(event.target.value);
      setError(null);
      resizeComposerField(event.target);
    },
    []
  );

  const handleSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      submitPrompt(message);
    },
    [message, submitPrompt]
  );

  const handleComposerKeyDown = useCallback(
    (event: KeyboardEvent<HTMLFormElement>) => {
      if (event.key !== "Enter" || event.shiftKey) {
        return;
      }

      event.preventDefault();
      submitPrompt(message);
    },
    [message, submitPrompt]
  );

  const handleFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const handleComposerBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && event.currentTarget.contains(nextTarget)) {
      return;
    }
    setFocused(false);
  }, []);

  const handleSuggestedPrompt = useCallback(
    (prompt: string) => {
      submitPrompt(prompt);
    },
    [submitPrompt]
  );

  const handleViewConversations = useCallback(() => {
    navigate("/chat-session");
  }, [navigate]);

  return {
    message,
    loading,
    error,
    focused,
    handleMessageChange,
    handleSubmit,
    handleComposerKeyDown,
    handleFocus,
    handleComposerBlur,
    handleSuggestedPrompt,
    handleViewConversations,
  };
}

export default useLlmChatPrompt;
